import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Reveal, Overline } from "../components/Reveal";
import { ProjectEnquiryForm } from "../components/EnquiryForms";
import api, { whatsappLink, unsplashSrc, unsplashSrcset } from "../lib/api";

const ListBlock = ({ title, items, testid }) =>
    items?.length ? (
        <Reveal>
            <h3 className="font-serif text-2xl font-medium tracking-tight text-ink md:text-3xl">{title}</h3>
            <ul className="mt-6 space-y-3" data-testid={testid}>
                {items.map((item, i) => (
                    <li key={i} className="flex gap-4 border-b border-ink/10 pb-3 text-sm font-light leading-relaxed text-ink/70">
                        <span className="mt-1 font-serif text-xs italic text-gold">{String(i + 1).padStart(2, "0")}</span>
                        {item}
                    </li>
                ))}
            </ul>
        </Reveal>
    ) : null;

const MetaItem = ({ label, value }) =>
    value ? (
        <div className="border-l border-gold/50 pl-4">
            <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-ink/45">{label}</p>
            <p className="mt-1 text-sm font-light text-ink">{value}</p>
        </div>
    ) : null;

const ProjectDetail = () => {
    const { slug } = useParams();
    const [project, setProject] = useState(null);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setProject(null);
        setNotFound(false);
        api.get(`/projects/${slug}`)
            .then((r) => setProject(r.data))
            .catch(() => setNotFound(true));
    }, [slug]);

    if (notFound)
        return (
            <main className="mx-auto max-w-[1400px] px-6 py-48 md:px-12" data-testid="project-not-found">
                <h1 className="font-serif text-4xl font-light text-ink">Project not found.</h1>
                <Link to="/opportunities" className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-gold">
                    <ArrowLeft className="h-4 w-4" /> Back to Opportunities
                </Link>
            </main>
        );

    if (!project)
        return (
            <main className="mx-auto max-w-[1400px] px-6 py-48 md:px-12" data-testid="project-loading">
                <div className="aspect-[21/9] animate-pulse bg-ink/5" />
            </main>
        );

    return (
        <main data-testid="project-detail-page">
            <section className="relative flex min-h-[70svh] items-end overflow-hidden bg-charcoal text-ivory">
                <img
                    src={unsplashSrc(project.hero_image, 1600)}
                    srcSet={unsplashSrcset(project.hero_image)}
                    sizes="100vw"
                    alt={project.name}
                    className="absolute inset-0 h-full w-full object-cover"
                    fetchpriority="high"
                    decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/35 to-charcoal/10" />
                <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 pb-16 pt-40 md:px-12">
                    <Reveal>
                        <Link
                            to="/opportunities"
                            data-testid="project-back-link"
                            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-ivory/60 transition-colors hover:text-gold"
                        >
                            <ArrowLeft className="h-4 w-4" /> Opportunities
                        </Link>
                        <p className="mt-8 text-[11px] font-medium uppercase tracking-[0.3em] text-gold">{project.market}</p>
                        <h1 className="mt-3 font-serif text-5xl font-light leading-[1.02] tracking-tight sm:text-6xl" data-testid="project-name">
                            {project.name}
                        </h1>
                        <p className="mt-3 text-sm font-light text-ivory/70">
                            {project.developer} &bull; {project.location}
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="mx-auto max-w-[1400px] px-6 py-16 md:px-12 md:py-24">
                <div className="grid grid-cols-2 gap-8 md:grid-cols-3 lg:grid-cols-6" data-testid="project-meta">
                    <MetaItem label="City" value={project.city} />
                    <MetaItem label="Location" value={project.location} />
                    <MetaItem label="Property Type" value={project.property_type} />
                    <MetaItem label="Configuration" value={project.configuration} />
                    <MetaItem label="Starting Price" value={project.starting_price} />
                    <MetaItem label="Possession" value={project.possession} />
                    <MetaItem label="Status" value={project.status} />
                </div>
                {project.rera && (
                    <p className="mt-8 border border-ink/10 bg-white px-6 py-4 text-[12px] font-light text-ink/60" data-testid="project-rera">
                        <span className="font-medium uppercase tracking-[0.2em] text-ink/45">RERA / Registration:&nbsp;</span>
                        {project.rera}
                    </p>
                )}

                <div className="mt-20 grid gap-16 md:grid-cols-12">
                    <div className="space-y-16 md:col-span-7">
                        <Reveal>
                            <Overline>Overview</Overline>
                            <p className="mt-5 text-base font-light leading-relaxed text-ink/75 md:text-lg" data-testid="project-overview">
                                {project.overview}
                            </p>
                        </Reveal>
                        <ListBlock title="Key Highlights" items={project.highlights} testid="project-highlights" />
                        <ListBlock title="Configurations" items={project.configurations} testid="project-configurations" />
                        <ListBlock title="Pricing" items={project.pricing} testid="project-pricing" />
                        <ListBlock title="Amenities" items={project.amenities} testid="project-amenities" />
                        <ListBlock title="Investment Considerations" items={project.investment_considerations} testid="project-investment-considerations" />
                        {project.location_info && (
                            <Reveal>
                                <h3 className="font-serif text-2xl font-medium tracking-tight text-ink md:text-3xl">Location</h3>
                                <p className="mt-5 text-sm font-light leading-relaxed text-ink/70" data-testid="project-location-info">
                                    {project.location_info}
                                </p>
                            </Reveal>
                        )}
                        <Reveal>
                            <h3 className="font-serif text-2xl font-medium tracking-tight text-ink md:text-3xl">About the Developer</h3>
                            <p className="mt-5 text-sm font-light leading-relaxed text-ink/70" data-testid="project-developer-info">
                                {project.developer_info}
                            </p>
                        </Reveal>
                    </div>

                    <div className="md:col-span-5">
                        <div className="md:sticky md:top-28">
                            <Reveal className="border border-ink/10 bg-white p-8 md:p-10">
                                <Overline>Enquire About This Project</Overline>
                                <h3 className="mt-4 font-serif text-3xl font-light tracking-tight text-ink">
                                    Interested in {project.name}?
                                </h3>
                                <p className="mt-3 text-sm font-light leading-relaxed text-ink/60">
                                    Share a few details and the LUXATI team will connect with you regarding
                                    current availability, pricing and relevant project information.
                                </p>
                                <a
                                    data-testid="project-whatsapp-cta"
                                    href={whatsappLink(`Hi LUXATI, I'm interested in ${project.name}. Please share the current details and availability.`)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-8 inline-flex w-full items-center justify-center gap-3 border border-ink/25 px-8 py-4 text-[11px] font-medium uppercase tracking-[0.22em] text-ink transition-colors duration-500 hover:border-gold hover:text-[#8a6a1c]"
                                >
                                    Enquire on WhatsApp
                                </a>
                                <div className="mt-8">
                                    <ProjectEnquiryForm
                                        testid="project-enquiry-form"
                                        context={{
                                            project: project.name,
                                            developer: project.developer,
                                            location: project.location,
                                            city: project.city,
                                            category: project.category,
                                            project_ref: project.slug,
                                            source_url: window.location.href,
                                        }}
                                    />
                                </div>
                            </Reveal>
                        </div>
                    </div>
                </div>

                {project.gallery?.length > 0 && (
                    <Reveal className="mt-24">
                        <Overline>Gallery</Overline>
                        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="project-gallery">
                            {project.gallery.map((src, i) => (
                                <div key={i} className="img-frame aspect-[4/3] bg-secondary">
                                    <img src={unsplashSrc(src, 960)} srcSet={unsplashSrcset(src)} sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw" alt={`${project.name} gallery ${i + 1}`} loading="lazy" decoding="async" />
                                </div>
                            ))}
                        </div>
                    </Reveal>
                )}

                <Reveal className="mt-20 border-t border-ink/10 pt-8">
                    <p className="max-w-3xl text-[12px] font-light leading-relaxed text-ink/45">
                        Project information, pricing and availability are subject to change and should be
                        independently confirmed at the time of enquiry. Imagery shown is representative
                        architectural photography, not project renders. LUXATI presents project information
                        for evaluation purposes; sales are conducted by the respective developers or their
                        authorised representatives.
                    </p>
                </Reveal>
            </section>
        </main>
    );
};

export default ProjectDetail;
